import { useState } from 'react';
import { Zap, Loader2, Settings as SettingsIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useActivityTemplates, type ActivityTemplate } from '@/data/hooks/useActivityTemplates';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { NextStepPickerSheet } from './NextStepPickerSheet';
import type { NextStep } from '@/types';
import { toast } from 'sonner';

interface Props {
  /** Deal to log against. If omitted, the chips render in "info" mode (no logging). */
  dealId?: string;
  customerName?: string;
  /** Called after a successful template fire, so the parent can refresh. */
  onLogged?: () => void;
}

export function TemplateChips({ dealId, customerName, onLogged }: Props) {
  const { mode, dealershipId, userId, role } = useAuth();
  const { templates, loading } = useActivityTemplates({ activeOnly: true });
  const [pending, setPending] = useState<ActivityTemplate | null>(null);
  const [busy, setBusy] = useState(false);

  const handleChipClick = (tpl: ActivityTemplate) => {
    if (!dealId) {
      toast.info('Open a deal to log activity from a template.');
      return;
    }
    setPending(tpl);
  };

  const fireTemplate = async (nextStep: NextStep) => {
    if (!pending || !dealId) return;
    if (mode !== 'real' || !dealershipId) {
      toast.success(`Logged: ${pending.name}`, { description: `Next: ${nextStep}` });
      setPending(null);
      onLogged?.();
      return;
    }
    setBusy(true);
    const nowIso = new Date().toISOString();
    const rows = pending.activity_types.map((t) => ({
      dealership_id: dealershipId,
      deal_id: dealId,
      rep_id: userId,
      activity_type: t,
      details: `Template: ${pending.name}`,
      customer_name: customerName ?? null,
    }));

    const { error: actErr } = await supabase.from('activities').insert(rows);
    if (actErr) {
      setBusy(false);
      toast.error('Failed to log template', { description: actErr.message });
      return;
    }
    const { error: dealErr } = await supabase
      .from('deals')
      .update({ last_activity_at: nowIso, next_step: nextStep, status: 'active' })
      .eq('id', dealId);
    setBusy(false);
    if (dealErr) {
      toast.error('Activities logged but deal not updated', { description: dealErr.message });
    } else {
      toast.success(`Logged: ${pending.name}`, {
        description: `Next: ${(nextStep as string).replace('_', ' ')}`,
      });
    }
    setPending(null);
    onLogged?.();
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground py-2">
        <Loader2 className="h-3 w-3 animate-spin" /> Loading templates…
      </div>
    );
  }

  if (templates.length === 0) {
    if (role === 'manager' || role === 'admin') {
      return (
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground py-2">
          <span>No activity templates yet.</span>
          <Link to="/settings" className="text-primary inline-flex items-center gap-1 hover:underline">
            <SettingsIcon className="h-3 w-3" /> Configure
          </Link>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <Zap className="h-3 w-3 text-primary" />
        <span>Quick templates</span>
      </div>
      <div className="flex flex-wrap gap-2 pb-1">
        {templates.map((tpl) => (
          <button
            key={tpl.id}
            type="button"
            onClick={() => handleChipClick(tpl)}
            disabled={busy}
            className="flex flex-col items-start gap-0.5 rounded-lg border border-border bg-card hover:bg-primary/10 hover:border-primary/40 hover:text-primary transition-colors px-3 py-2 text-xs font-medium disabled:opacity-50 min-w-0"
          >
            <span className="inline-flex items-center gap-1.5 max-w-full">
              <Zap className="h-3 w-3 shrink-0" />
              <span className="truncate">{tpl.name}</span>
            </span>
            {tpl.activity_types.length > 0 && (
              <span className="text-[10px] text-muted-foreground font-normal pl-4 truncate max-w-full">
                {tpl.activity_types.map(t => t.replace(/_/g, ' ')).join(' + ')}
                {tpl.activity_types.length > 1 && ` · logs ${tpl.activity_types.length}`}
              </span>
            )}
          </button>
        ))}
      </div>
      <NextStepPickerSheet
        open={!!pending}
        onOpenChange={(v) => { if (!v) setPending(null); }}
        templateName={pending?.name}
        onConfirm={fireTemplate}
        busy={busy}
      />
    </div>
  );
}